import React from 'react';
import { createBrowserRouter, Navigate, Outlet, useLocation, useRouteError } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { ADMIN_EMAILS } from './constants/brand';

// Layout wrapper
import { NetworkRecoveryBar } from './components/logic/NetworkRecoveryBar';
import { RoleSwitcher } from './components/demo/RoleSwitcher';

// Auth Pages
import { SignInPage } from './pages/auth/SignInPage';
import { SignUpPage } from './pages/auth/SignUpPage';
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage';

// Shopper Pages
import { HomePage } from './pages/shopper/HomePage';
import { ProductDetailPage } from './pages/shopper/ProductDetailPage';
import { BoutiquePage } from './pages/shopper/BoutiquePage';
import { BoutiquesListPage } from './pages/shopper/BoutiquesListPage';
import { SearchResultsPage } from './pages/shopper/SearchResultsPage';
import { BagPage } from './pages/shopper/BagPage';
import { OrderTrackingPage } from './pages/shopper/OrderTrackingPage';
import { OrdersPage } from './pages/shopper/OrdersPage';
import { SettingsPage } from './pages/shopper/SettingsPage';
import { NotificationPreferencesPage } from './pages/shopper/NotificationPreferencesPage';
import { SecurityPage } from './pages/shopper/SecurityPage';
import { ProfilePage } from './pages/shopper/ProfilePage';
import { AddressesPage } from './pages/shopper/AddressesPage';
import { PaymentMethodsPage } from './pages/shopper/PaymentMethodsPage';
import { LanguagePage } from './pages/shopper/LanguagePage';
import { HelpPage } from './pages/shopper/HelpPage';
import { AboutPage } from './pages/shopper/AboutPage';
import { DisputePage } from './pages/shopper/DisputePage';
import { LikesPage } from './pages/shopper/LikesPage';
import { FAQPage } from './pages/shopper/FAQPage';
import { CategoriesPage } from './pages/shopper/CategoriesPage';
import { SalePage } from './pages/shopper/SalePage';
import { ShippingPage } from './pages/shopper/ShippingPage';
import { ReturnsPage } from './pages/shopper/ReturnsPage';
import { ContactPage } from './pages/shopper/ContactPage';
import { CareersPage } from './pages/shopper/CareersPage';
import { PressPage } from './pages/shopper/PressPage';
import { BlogPage } from './pages/shopper/BlogPage';
import { BlogPostPage } from './pages/shopper/BlogPostPage';

// Business Pages
import { BusinessDashboard } from './pages/business/BusinessDashboard';
import { BoutiqueOnboardingPage } from './pages/business/BoutiqueOnboardingPage';
import { BusinessAnalyticsDashboard } from './pages/business/BusinessAnalyticsDashboard';
import { StoreHoursPage } from './pages/business/StoreHoursPage';
import { BusinessSettingsPage } from './pages/business/BusinessSettingsPage';
import { BusinessLayout } from './components/business/BusinessLayout';
import { SeatInvitePage } from './pages/business/SeatInvitePage';
import { SeatSalesPage } from './pages/business/SeatSalesPage';
import { SeatProfilePage } from './pages/business/SeatProfilePage';
import { useBusinessContext } from './hooks/useBusinessContext';

// Shopify Connect (public shareable install link)
import { ConnectShopifyPage } from './pages/shopify/ConnectShopifyPage';
import { ConnectShopifyFinishPage } from './pages/shopify/ConnectShopifyFinishPage';

// Driver Pages
import { DriverDashboard } from './pages/driver/DriverDashboard';

// Landing / Coming Soon
import { LandingPage } from './pages/LandingPage';

// Changelog
import { ChangelogPage } from './pages/ChangelogPage';

// Legal
import { MerchantAgreementPage } from './pages/legal/MerchantAgreementPage';
import { TermsOfServicePage } from './pages/legal/TermsOfServicePage';
import { PrivacyPolicyPage } from './pages/legal/PrivacyPolicyPage';

// Admin Pages
import { AdminLayout } from './pages/admin/AdminLayout';
import { AdminDashboard } from './pages/admin/AdminDashboard';
import { AdminOrders } from './pages/admin/AdminOrders';
import { AdminUsers } from './pages/admin/AdminUsers';
import { AdminBusinesses } from './pages/admin/AdminBusinesses';
import { AdminDrivers } from './pages/admin/AdminDrivers';
import { AdminDisputes } from './pages/admin/AdminDisputes';
import { AdminPayoutHolds } from './pages/admin/AdminPayoutHolds';
import { AdminAbandonment } from './pages/admin/AdminAbandonment';
import { AdminSettings } from './pages/admin/AdminSettings';
import { AdminApiKeys } from './pages/admin/AdminApiKeys';
import { AdminTeam } from './pages/admin/AdminTeam';
import { AdminInbox } from './pages/admin/AdminInbox';
import { AdminWaitlist } from './pages/admin/AdminWaitlist';

// Protected Route wrapper
function ProtectedRoute({ children, allowedRoles }: { 
  children: React.ReactNode;
  allowedRoles?: string[];
}) {
  const { user, userProfile, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-pulse">
            <h1 className="text-6xl font-serif text-primary">F</h1>
          </div>
          <p className="mt-4 text-body font-sans">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/signin" replace />;
  }

  // A missing profile is NOT a free pass — deny role-gated routes until the
  // profile exists and the role matches.
  if (allowedRoles && (!userProfile || !allowedRoles.includes(userProfile.role))) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

// Auth Route wrapper (redirect if already logged in)
function AuthRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-pulse">
            <h1 className="text-6xl font-serif text-primary">F</h1>
          </div>
        </div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

// Business Route wrapper — must be signed in as an owner, a seat, or an admin.
// Wraps children in BusinessLayout so portal-wide notices (e.g. the payout
// hold banner) persist across every business page rather than only the one
// they were added to.
// `ownerOnly` gates the pages that expose money, tax, or store-wide data;
// seats are bounced back to the dashboard rather than the marketing site.
function BusinessRoute({
  children,
  ownerOnly = false,
}: {
  children: React.ReactNode;
  ownerOnly?: boolean;
}) {
  const { user, userProfile, loading } = useAuth();
  const { isSeat, loading: contextLoading } = useBusinessContext();

  if (loading || contextLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-pulse">
            <h1 className="text-6xl font-serif text-primary">F</h1>
          </div>
          <p className="mt-4 text-body font-sans">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/signin" replace />;
  }

  const isAdmin = user.email && ADMIN_EMAILS.includes(user.email.toLowerCase());
  const isStaff = userProfile?.role === 'business_staff' || isSeat;
  // Null profile is denied — only a real business/seat profile (or admin) gets in
  if (!isAdmin && userProfile?.role !== 'business' && !isStaff) {
    return <Navigate to="/" replace />;
  }

  if (ownerOnly && isStaff && !isAdmin) {
    return <Navigate to="/business/dashboard" replace />;
  }

  // Seats join an already-onboarded store, so onboarding never applies to them.
  if (userProfile?.role === 'business' && !(userProfile as any).onboardingComplete) {
    return <Navigate to="/business/onboarding" replace />;
  }

  return <BusinessLayout>{children}</BusinessLayout>;
}

// Admin Route wrapper — founders by email, plus anyone a founder has promoted.
// Promoted admins carry the `admin` claim and role 'admin'; the email list is
// only a bootstrap so founders are never locked out before claims sync.
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, userProfile, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-pulse">
            <h1 className="text-6xl font-serif text-primary">F</h1>
          </div>
          <p className="mt-4 text-body font-sans">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/signin" replace />;
  }

  const isFounder = !!user.email && ADMIN_EMAILS.includes(user.email.toLowerCase());
  if (!isFounder && userProfile?.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

// Driver Route wrapper — must be signed in as driver or admin
function DriverRoute({ children }: { children: React.ReactNode }) {
  const { user, userProfile, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-pulse">
            <h1 className="text-6xl font-serif text-primary">F</h1>
          </div>
          <p className="mt-4 text-body font-sans">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/signin" replace />;
  }

  const isAdmin = user.email && ADMIN_EMAILS.includes(user.email.toLowerCase());
  // Null profile is denied — only a real 'driver' profile (or admin) gets in
  if (!isAdmin && userProfile?.role !== 'driver') {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

// Landing gate — visitors see the waitlist; signed-in users go straight
// to the app surface that matches their role.
function LandingGate() {
  const { user, userProfile, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-pulse">
            <h1 className="text-6xl font-serif text-primary">F</h1>
          </div>
        </div>
      </div>
    );
  }

  if (user) {
    const isAdmin = user.email && ADMIN_EMAILS.includes(user.email.toLowerCase());
    if (isAdmin) return <Navigate to="/admin" replace />;
    if (userProfile?.role === 'business') return <Navigate to="/business" replace />;
    if (userProfile?.role === 'driver') return <Navigate to="/driver" replace />;
    return <Navigate to="/shop" replace />;
  }

  return <LandingPage />;
}

// Root Layout
function RootLayout() {
  const location = useLocation();
  const adminPathMatch = location.pathname.match(/^\/admin(\/.*)?$/i);
  if (adminPathMatch && !location.pathname.startsWith('/admin')) {
    const suffix = adminPathMatch[1] ?? '';
    return (
      <Navigate
        to={`/admin${suffix}${location.search}${location.hash}`}
        replace
      />
    );
  }

  return (
    <>
      <NetworkRecoveryBar />
      <Outlet />
      <RoleSwitcher />
    </>
  );
}

// Wrapper components for pages that need navigation props
function HomePageWrapper() {
  return <HomePage />;
}

function ProductDetailWrapper() {
  return <ProductDetailPage />;
}

function BagPageWrapper() {
  return <BagPage />;
}

function OrderTrackingWrapper() {
  return <OrderTrackingPage />;
}

function AdminAnalyticsWrapper() {
  const nav = React.useCallback((page: string) => {
    if (page !== 'analytics') {
      window.location.href = `/admin`;
    }
  }, []);

  return (
    <AdminLayout currentPage="analytics" onNavigate={nav}>
      <BusinessAnalyticsDashboard />
    </AdminLayout>
  );
}

function AdminPageWrapper() {
  const [currentPage, setCurrentPage] = React.useState('dashboard');
  
  const renderAdminContent = () => {
    switch (currentPage) {
      case 'dashboard':
        return <AdminDashboard onNavigate={setCurrentPage} />;
      case 'analytics':
        return <BusinessAnalyticsDashboard />;
      case 'orders':
        return <AdminOrders />;
      case 'users':
        return <AdminUsers />;
      case 'businesses':
        return <AdminBusinesses />;
      case 'drivers':
        return <AdminDrivers />;
      case 'disputes':
        return <AdminDisputes />;
      case 'payout-holds':
        return <AdminPayoutHolds />;
      case 'abandonment':
        return <AdminAbandonment />;
      case 'inbox':
        return <AdminInbox />;
      case 'waitlist':
        return <AdminWaitlist />;
      case 'api-keys':
        return <AdminApiKeys />;
      case 'team':
        return <AdminTeam />;
      case 'settings':
        return <AdminSettings />;
      default:
        return <AdminDashboard />;
    }
  };
  
  return (
    <AdminLayout currentPage={currentPage} onNavigate={setCurrentPage}>
      {renderAdminContent()}
    </AdminLayout>
  );
}

function RootErrorBoundary() {
  const error = useRouteError() as Error;
  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <div className="text-center max-w-md">
        <h1 className="text-5xl font-serif text-primary mb-4">Oops</h1>
        <p className="text-lg text-heading mb-2">Something went wrong</p>
        <p className="text-sm text-body mb-6">
          {error?.message || 'An unexpected error occurred.'}
        </p>
        <button
          onClick={() => window.location.href = '/'}
          className="px-6 py-2.5 bg-primary text-white rounded-full font-medium hover:opacity-90 transition-opacity"
        >
          Go Home
        </button>
      </div>
    </div>
  );
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    errorElement: <RootErrorBoundary />,
    children: [
      // Public Routes
      {
        index: true,
        element: <LandingGate />,
      },
      {
        path: 'shop',
        element: <HomePageWrapper />,
      },
      {
        path: 'product/:productId',
        element: <ProductDetailWrapper />,
      },
      {
        path: 'boutiques',
        element: <BoutiquesListPage />,
      },
      {
        path: 'boutique/:boutiqueId',
        element: <BoutiquePage />,
      },
      {
        path: 'search',
        element: <SearchResultsPage />,
      },
      {
        path: 'likes',
        element: <LikesPage />,
      },
      {
        path: 'changelog',
        element: <ChangelogPage />,
      },
      {
        path: 'faq',
        element: <FAQPage />,
      },
      {
        path: 'categories',
        element: <CategoriesPage />,
      },
      {
        path: 'sale',
        element: <SalePage />,
      },
      {
        path: 'shipping',
        element: <ShippingPage />,
      },
      {
        path: 'returns',
        element: <ReturnsPage />,
      },
      {
        path: 'contact',
        element: <ContactPage />,
      },
      {
        path: 'careers',
        element: <CareersPage />,
      },
      {
        path: 'press',
        element: <PressPage />,
      },
      {
        path: 'blog',
        element: <BlogPage />,
      },
      {
        path: 'blog/:slug',
        element: <BlogPostPage />,
      },
      {
        path: 'coming-soon',
        element: <LandingPage />,
      },
      
      // Auth Routes (redirect if logged in)
      {
        path: 'signin',
        element: (
          <AuthRoute>
            <SignInPage />
          </AuthRoute>
        ),
      },
      {
        path: 'signup',
        element: (
          <AuthRoute>
            <SignUpPage />
          </AuthRoute>
        ),
      },
      {
        path: 'forgot-password',
        element: (
          <AuthRoute>
            <ForgotPasswordPage />
          </AuthRoute>
        ),
      },

      // Seat invitation — public link, works signed in or out
      {
        path: 'seat-invite/:token',
        element: <SeatInvitePage />,
      },

      // Shopify Connect — public shareable install link (works logged in or out)
      {
        path: 'connect/shopify',
        element: <ConnectShopifyPage />,
      },
      {
        path: 'connect/shopify/finish',
        element: <ConnectShopifyFinishPage />,
      },
      
      // Protected Shopper Routes
      {
        path: 'bag',
        element: (
          <ProtectedRoute>
            <BagPageWrapper />
          </ProtectedRoute>
        ),
      },
      {
        path: 'orders',
        element: (
          <ProtectedRoute>
            <OrdersPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'orders/:orderId',
        element: (
          <ProtectedRoute>
            <OrderTrackingWrapper />
          </ProtectedRoute>
        ),
      },
      {
        path: 'orders/:orderId/dispute',
        element: (
          <ProtectedRoute>
            <DisputePage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'settings',
        element: (
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'settings/notifications',
        element: (
          <ProtectedRoute>
            <NotificationPreferencesPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'settings/security',
        element: (
          <ProtectedRoute>
            <SecurityPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'settings/profile',
        element: (
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'settings/addresses',
        element: (
          <ProtectedRoute>
            <AddressesPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'settings/payment',
        element: (
          <ProtectedRoute>
            <PaymentMethodsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'settings/language',
        element: (
          <ProtectedRoute>
            <LanguagePage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'help',
        element: <HelpPage />,
      },
      {
        path: 'about',
        element: <AboutPage />,
      },
      {
        path: 'terms',
        element: <TermsOfServicePage />,
      },
      {
        path: 'privacy',
        element: <PrivacyPolicyPage />,
      },
      {
        path: 'merchant-agreement',
        element: <MerchantAgreementPage />,
      },
      
      // Business Portal Routes
      {
        path: 'business',
        element: (
          <BusinessRoute>
            <BusinessDashboard />
          </BusinessRoute>
        ),
      },
      {
        path: 'business/analytics',
        element: (
          <BusinessRoute ownerOnly>
            <BusinessAnalyticsDashboard />
          </BusinessRoute>
        ),
      },
      {
        path: 'business/my-sales',
        element: (
          <BusinessRoute>
            <SeatSalesPage />
          </BusinessRoute>
        ),
      },
      {
        path: 'business/profile',
        element: (
          <BusinessRoute>
            <SeatProfilePage />
          </BusinessRoute>
        ),
      },
      {
        path: 'business/hours',
        element: (
          <BusinessRoute>
            <StoreHoursPage />
          </BusinessRoute>
        ),
      },
      {
        path: 'business/settings',
        element: (
          <BusinessRoute ownerOnly>
            <BusinessSettingsPage />
          </BusinessRoute>
        ),
      },
      {
        path: 'business/onboarding',
        element: (
          <ProtectedRoute allowedRoles={['business']}>
            <BoutiqueOnboardingPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'business/*',
        element: (
          <BusinessRoute>
            <BusinessDashboard />
          </BusinessRoute>
        ),
      },
      
      // Driver App Routes
      {
        path: 'driver',
        element: (
          <DriverRoute>
            <DriverDashboard />
          </DriverRoute>
        ),
      },
      {
        path: 'driver/*',
        element: (
          <DriverRoute>
            <DriverDashboard />
          </DriverRoute>
        ),
      },
      
      // Admin Routes — only accessible to admin role
      {
        path: 'admin',
        element: (
          <AdminRoute>
            <AdminPageWrapper />
          </AdminRoute>
        ),
      },
      {
        path: 'admin/analytics',
        element: (
          <AdminRoute>
            <AdminAnalyticsWrapper />
          </AdminRoute>
        ),
      },
      {
        path: 'admin/*',
        element: (
          <AdminRoute>
            <AdminPageWrapper />
          </AdminRoute>
        ),
      },
      
      // 404 Fallback
      {
        path: '*',
        element: (
          <div className="min-h-screen bg-white flex items-center justify-center">
            <div className="text-center">
              <h1 className="text-6xl font-serif text-primary mb-4">404</h1>
              <p className="text-xl text-heading mb-2">Page Not Found</p>
              <p className="text-body mb-8">The page you're looking for doesn't exist.</p>
              <a href="/" className="text-primary font-medium hover:underline">
                Go Home
              </a>
            </div>
          </div>
        ),
      },
    ],
  },
]);
